import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useData } from '../../contexts/DataContext';
import { MusicIcon, RejectModal } from '../../components/shared';
import { useTranslation } from 'react-i18next';

const PendingReleases: React.FC = () => {
  const { releases, updateReleaseStatus, updateRelease } = useData();
  const [activeTab, setActiveTab] = useState<'pending' | 'submitted' | 'rejected' | 'approved' | 'published'>('pending');
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [selectedReleaseForReject, setSelectedReleaseForReject] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest'>('newest'); // Sıralama durumu
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(25);
  
  // ISRC/UPC modal states  
  const [isrcModalOpen, setIsrcModalOpen] = useState(false);
  const [selectedReleaseForIsrc, setSelectedReleaseForIsrc] = useState<string | null>(null);
  const [isrcInput, setIsrcInput] = useState('');
  const [upcInput, setUpcInput] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  
  // Local state override for immediate UI updates
  const [localRightsUpdates, setLocalRightsUpdates] = useState<Record<string, {isrc?: string, upc?: string}>>({});
  const [localStatusUpdates, setLocalStatusUpdates] = useState<Record<string, string>>({});
  
  const { t } = useTranslation();
  
  // Apply local status updates to filtering
  const getEffectiveStatus = (release: any) => {
    return localStatusUpdates[release.id] || release.status;
  };

  const pendingReleases = releases.filter(r => getEffectiveStatus(r) === 'pending');
  const submittedReleases = releases.filter(r => getEffectiveStatus(r) === 'submitted');
  const rejectedReleases = releases.filter(r => getEffectiveStatus(r) === 'rejected');
  const approvedReleases = releases.filter(r => getEffectiveStatus(r) === 'approved');
  const publishedReleases = releases.filter(r => getEffectiveStatus(r) === 'published');

  const handleApprove = async (releaseId: string) => {
    // IMMEDIATELY update local state for instant UI feedback
    setLocalStatusUpdates(prev => ({
      ...prev,
      [releaseId]: 'approved'
    }));

    try {
      // Admin onayladığında şarkı onaylanıyor (approved durumuna geçiyor)
      await updateReleaseStatus(releaseId, 'approved');
    } catch (error) {
      console.error('Onaylama hatası:', error);
      // Revert local state on error
      setLocalStatusUpdates(prev => {
        const newState = { ...prev };
        delete newState[releaseId];
        return newState;
      });
      alert('Onaylama sırasında hata oluştu.');
    }
  };

  const handleReject = (releaseId: string) => {
    setSelectedReleaseForReject(releaseId);
    setRejectModalOpen(true);
  };

  const handleRejectConfirm = async (reason: string) => {
    if (selectedReleaseForReject) {
      // IMMEDIATELY update local state for instant UI feedback
      setLocalStatusUpdates(prev => ({
        ...prev,
        [selectedReleaseForReject]: 'rejected'
      }));

      setRejectModalOpen(false);
      setSelectedReleaseForReject(null);

      try {
        await updateReleaseStatus(selectedReleaseForReject, 'rejected', reason);
      } catch (error) {
        console.error('Reddetme hatası:', error);
        // Revert local state on error
        setLocalStatusUpdates(prev => {
          const newState = { ...prev };
          delete newState[selectedReleaseForReject];
          return newState;
        });
        alert('Reddetme sırasında hata oluştu.');
      }
    }
  };

  const handleRejectCancel = () => {
    setRejectModalOpen(false);
    setSelectedReleaseForReject(null);
  };

  const handlePublish = async (releaseId: string) => {
    // IMMEDIATELY update local state for instant UI feedback
    setLocalStatusUpdates(prev => ({
      ...prev,
      [releaseId]: 'published'
    }));

    try {
      // Bu fonksiyon gelecekte API'den gelen "published" durumu için kullanılacak
      await updateReleaseStatus(releaseId, 'published');
    } catch (error) {
      console.error('Yayınlama hatası:', error);
      // Revert local state on error
      setLocalStatusUpdates(prev => {
        const newState = { ...prev };
        delete newState[releaseId];
        return newState;
      });
      alert('Yayınlama sırasında hata oluştu.');
    }
  };

  const handleSubmitToPlatform = async (releaseId: string) => {
    // IMMEDIATELY update local state for instant UI feedback
    setLocalStatusUpdates(prev => ({
      ...prev,
      [releaseId]: 'submitted'
    }));

    console.log('✅ UI anında güncellendi, veritabanı güncelleniyor...');

    try {
      // Update status in background
      await updateReleaseStatus(releaseId, 'submitted');
      console.log('✅ Veritabanı güncellendi!');
    } catch (error) {
      console.error('Status güncelleme hatası:', error);
      // Revert local state on error
      setLocalStatusUpdates(prev => {
        const newState = { ...prev };
        delete newState[releaseId];
        return newState;
      });
      alert('Durum güncelleme sırasında hata oluştu.');
    }
  };

  const handleAddIsrcUpc = (releaseId: string) => {
    const release = releases.find(r => r.id === releaseId);
    if (!release) return;
    
    // Check local updates first, then database
    const localUpdate = localRightsUpdates[releaseId];
    if (localUpdate) {
      setIsrcInput(localUpdate.isrc || '');
      setUpcInput(localUpdate.upc || '');
    } else {
      // Rights might be an array from Supabase join, get first element
      const rights = Array.isArray(release.rights) ? release.rights[0] : release.rights;
      if (rights) {
        setIsrcInput(rights.isrc || '');
        setUpcInput(rights.upc || '');
      } else {
        setIsrcInput('');
        setUpcInput('');
      }
    }
    setSelectedReleaseForIsrc(releaseId);
    setIsrcModalOpen(true);
  };

  const handleIsrcUpcSave = async () => {
    if (!selectedReleaseForIsrc || isUpdating) return;

    const release = releases.find(r => r.id === selectedReleaseForIsrc);
    if (!release) return;

    setIsUpdating(true);

    try {
      const newIsrc = isrcInput.trim();
      const newUpc = upcInput.trim();
      
      // IMMEDIATELY update local state for instant UI feedback
      setLocalRightsUpdates(prev => ({
        ...prev,
        [selectedReleaseForIsrc]: {
          isrc: newIsrc,
          upc: newUpc
        }
      }));
      
      console.log('✅ UI anında güncellendi, veritabanı güncelleniyor...');
      
      // Modal'ı hemen kapat
      setIsrcModalOpen(false);
      setSelectedReleaseForIsrc(null);
      setIsrcInput('');
      setUpcInput('');

      // Update the release with ISRC/UPC info in background
      const updatedRights = {
        release_id: selectedReleaseForIsrc,
        isrc: newIsrc,
        upc: newUpc,
        // Force Distnary as owner regardless of API data
        phonogram_owner: 'Distnary',
        composition_owner: 'Distnary'
      };

      const success = await updateRelease(selectedReleaseForIsrc, { rights: updatedRights });
      
      if (success) {
        console.log('✅ Veritabanı güncellendi!');
      } else {
        // Revert local state if database update fails
        setLocalRightsUpdates(prev => {
          const newState = { ...prev };
          delete newState[selectedReleaseForIsrc];
          return newState;
        });
        alert('ISRC/UPC güncelleme başarısız oldu. Lütfen tekrar deneyin.');
      }
      
    } catch (error) {
      console.error('ISRC/UPC güncelleme hatası:', error);
      // Revert local state on error
      setLocalRightsUpdates(prev => {
        const newState = { ...prev };
        delete newState[selectedReleaseForIsrc!];
        return newState;
      });
      alert('ISRC/UPC güncelleme sırasında hata oluştu.');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleIsrcUpcCancel = () => {
    setIsrcModalOpen(false);
    setSelectedReleaseForIsrc(null);
    setIsrcInput('');
    setUpcInput('');
  };


  const getCurrentReleases = () => {
    let currentList;
    switch (activeTab) {
      case 'pending':
        currentList = pendingReleases;
        break;
      case 'submitted':
        currentList = submittedReleases;
        break;
      case 'rejected':
        currentList = rejectedReleases;
        break;
      case 'approved':
        currentList = approvedReleases;
        break;
      case 'published':
        currentList = publishedReleases;
        break;
      default:
        currentList = pendingReleases;
    }

    // Sort by created_at date
    return [...currentList].sort((a, b) => {
      const dateA = new Date(a.created_at).getTime();
      const dateB = new Date(b.created_at).getTime();

      if (sortOrder === 'newest') {
        return dateB - dateA; // En yeni başta
      } else {
        return dateA - dateB; // En eski başta
      }
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30';
      case 'submitted':
        return 'bg-blue-500/20 text-blue-300 border-blue-500/30';
      case 'approved':
        return 'bg-green-500/20 text-green-300 border-green-500/30';
      case 'rejected':
        return 'bg-red-500/20 text-red-300 border-red-500/30';
      case 'published':
        return 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30';
      default:
        return 'bg-gray-500/20 text-gray-300 border-gray-500/30';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending':
        return t('status.pending');
      case 'submitted':
        return t('status.submitted');
      case 'approved':
        return t('status.approved');
      case 'rejected':
        return t('status.rejected');
      case 'published':
        return t('status.published');
      default:
        return status;
    }
  };

  const allCurrentReleases = getCurrentReleases();

  // Pagination hesaplamaları
  const totalPages = Math.ceil(allCurrentReleases.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentReleases = allCurrentReleases.slice(startIndex, endIndex);

  // Tab değiştiğinde sayfa numarasını resetle
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab]);

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl lg:text-3xl font-bold text-white">{t('admin.trackManagement')}</h1>
        <p className="mt-1 text-sm text-white/70">
          {t('submitStatus.trackSubmissionFlow')}
        </p>
      </div>

      {/* Tabs */}
      <div className="mb-6">
        <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-xl p-2">
          <nav className="flex flex-col sm:flex-row gap-2">
            <button
              onClick={() => setActiveTab('pending')}
              className={`flex-1 py-3 px-4 text-sm font-medium rounded-lg transition-all duration-200 ${
                activeTab === 'pending'
                  ? 'bg-orange-500 text-white shadow-lg'
                  : 'text-white/70 hover:text-white hover:bg-white/10'
              }`}
            >
              {t('admin.pendingApprovals')} ({pendingReleases.length})
            </button>
            <button
              onClick={() => setActiveTab('submitted')}
              className={`flex-1 py-3 px-4 text-sm font-medium rounded-lg transition-all duration-200 ${
                activeTab === 'submitted'
                  ? 'bg-orange-500 text-white shadow-lg'
                  : 'text-white/70 hover:text-white hover:bg-white/10'
              }`}
            >
              {t('submitStatus.submittedTracks')} ({submittedReleases.length})
            </button>
            <button
              onClick={() => setActiveTab('approved')}
              className={`flex-1 py-3 px-4 text-sm font-medium rounded-lg transition-all duration-200 ${
                activeTab === 'approved'
                  ? 'bg-orange-500 text-white shadow-lg'
                  : 'text-white/70 hover:text-white hover:bg-white/10'
              }`}
            >
              {t('admin.approved')} ({approvedReleases.length})
            </button>
            <button
              onClick={() => setActiveTab('rejected')}
              className={`flex-1 py-3 px-4 text-sm font-medium rounded-lg transition-all duration-200 ${
                activeTab === 'rejected'
                  ? 'bg-orange-500 text-white shadow-lg'
                  : 'text-white/70 hover:text-white hover:bg-white/10'
              }`}
            >
              {t('admin.rejected')} ({rejectedReleases.length})
            </button>
            <button
              onClick={() => setActiveTab('published')}
              className={`flex-1 py-3 px-4 text-sm font-medium rounded-lg transition-all duration-200 ${
                activeTab === 'published'
                  ? 'bg-orange-500 text-white shadow-lg'
                  : 'text-white/70 hover:text-white hover:bg-white/10'
              }`}
            >
              {t('admin.published')} ({publishedReleases.length})
            </button>
          </nav>
        </div>
      </div>

      {/* Sort & Items Per Page Filter */}
      <div className="mb-6 flex flex-col sm:flex-row justify-between gap-4">
        {/* Total Count Info */}
        <div className="flex items-center bg-white/10 backdrop-blur-lg border border-white/20 rounded-xl px-4 py-2">
          <span className="text-sm text-white/70">Toplam: </span>
          <span className="ml-2 text-lg font-bold text-orange-400">{allCurrentReleases.length}</span>
          <span className="ml-1 text-sm text-white/70">kayıt</span>
        </div>

        <div className="flex flex-wrap gap-3 justify-center sm:justify-end">
          {/* Items Per Page Selector */}
          <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-lg border border-white/20 rounded-xl p-2">
            <span className="text-sm text-white/70 ml-2">Göster:</span>
            {[25, 50, 100].map((count) => (
              <button
                key={count}
                onClick={() => { setItemsPerPage(count); setCurrentPage(1); }}
                className={`px-3 py-1 text-sm font-medium rounded-lg transition-all duration-200 ${
                  itemsPerPage === count
                    ? 'bg-orange-500 text-white shadow-lg'
                    : 'text-white/70 hover:text-white hover:bg-white/10'
                }`}
              >
                {count}
              </button>
            ))}
          </div>

          {/* Sort Order */}
          <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-3 bg-white/10 backdrop-blur-lg border border-white/20 rounded-xl p-2">
            <span className="text-sm text-white/70 font-medium ml-2">Sıralama:</span>
            <div className="flex gap-2">
              <button
                onClick={() => setSortOrder('newest')}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                  sortOrder === 'newest'
                    ? 'bg-orange-500 text-white shadow-lg'
                    : 'text-white/70 hover:text-white hover:bg-white/10'
                }`}
              >
                En Yeni
              </button>
              <button
                onClick={() => setSortOrder('oldest')}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                  sortOrder === 'oldest'
                    ? 'bg-orange-500 text-white shadow-lg'
                    : 'text-white/70 hover:text-white hover:bg-white/10'
                }`}
              >
                En Eski
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Info Banner */}
      {activeTab === 'submitted' && (
        <div className="mb-6 p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-blue-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <p className="text-sm text-blue-300 font-medium">{t('submitStatus.submittedTracks')}</p>
              <p className="text-xs text-blue-400 mt-1">
                {t('submitStatus.submittedInfo')}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="space-y-6">
        {currentReleases.length === 0 ? (
          <div className="glass-card">
            <div className="text-center py-12">
              <svg className="mx-auto h-12 w-12 text-white/40" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-white">
                {activeTab === 'pending' && t('admin.noPendingApprovals')}
                {activeTab === 'submitted' && t('submitStatus.noSubmittedTracks')}
                {activeTab === 'approved' && t('admin.noApprovedMusic')}
                {activeTab === 'rejected' && t('admin.noRejectedMusic')}
                {activeTab === 'published' && t('admin.noPublishedMusic')}
              </h3>
              <p className="mt-1 text-sm text-white/60">
                {activeTab === 'pending' && t('admin.noPendingCurrently')}
                {activeTab === 'submitted' && t('submitStatus.noSubmittedCurrently')}
                {activeTab === 'approved' && t('admin.noApprovedCurrently')}
                {activeTab === 'rejected' && t('admin.noRejectedCurrently')}
                {activeTab === 'published' && t('admin.noPublishedCurrently')}
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {currentReleases.map((release) => (
              <div key={release.id} className="glass-card">
                <div className="flex items-start space-x-4">
                  {release.cover_art ? (
                    <img
                      src={release.cover_art}
                      alt={release.title}
                      className="w-20 h-20 rounded-xl object-cover flex-shrink-0 border border-white/20"
                    />
                  ) : (
                    <div className="w-20 h-20 flex items-center justify-center border border-white/20 rounded-xl">
                      <MusicIcon size="md" />
                    </div>
                  )}
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="text-lg font-medium text-white">{release.title}</h3>
                        <p className="text-sm text-white/70">
                          {release.artist_display_name} • {release.type}
                        </p>
                        <p className="text-sm text-white/60">
                          {release.genre} • {new Date(release.release_date).toLocaleDateString('tr-TR')}
                        </p>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        {/* Test Badge - if is_test is true */}
                        {release.is_test && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-500/20 text-purple-300 border border-purple-500/30">
                            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                            TEST
                          </span>
                        )}
                        
                        {/* Status Badge */}
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(getEffectiveStatus(release))}`}>
                          {getStatusText(getEffectiveStatus(release))}
                        </span>
                      </div>
                    </div>
                    
                    <div className="mt-4">
                      <h4 className="text-sm font-medium text-white mb-2">{t('admin.songs')}</h4>
                      <div className="space-y-1">
                        {release.tracks.map((track, index) => (
                          <div key={track.id} className="text-sm text-white/70 p-2 bg-white/5 rounded-lg">
                            {index + 1}. {track.title} ({track.duration})
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    {release.description && (
                      <div className="mt-4">
                        <h4 className="text-sm font-medium text-white">{t('tracks.description')}:</h4>
                        <p className="text-sm text-white/70 mt-1 p-3 bg-white/5 rounded-lg">{release.description}</p>
                      </div>
                    )}
                    
                    <div className="mt-4">
                      <h4 className="text-sm font-medium text-white mb-2">{t('tracks.distributionChannels')}:</h4>
                      <div className="flex flex-wrap gap-2">
                        {release.partners?.map((partner) => (
                          <span key={partner} className="inline-flex items-center px-3 py-1 rounded-full text-xs bg-blue-500/20 text-blue-300 border border-blue-500/30">
                            {partner}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="mt-6 pt-6 border-t border-white/20">
                  <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 mb-4 border border-white/10">
                    <h4 className="text-sm font-medium text-white mb-3 flex items-center">
                      <svg className="w-4 h-4 mr-2 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      {t('admin.adminInfo')}
                    </h4>
                    {release.app_info && (
                      <div className="mb-3 p-3 bg-blue-500/10 rounded-lg border border-blue-500/30">
                        <span className="text-blue-300 font-medium">Kaynak Uygulama:</span>
                        <span className="ml-2 text-blue-200 font-mono">{release.app_info}</span>
                      </div>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div className="p-3 bg-white/5 rounded-lg">
                        <span className="text-white/70">ISRC:</span>
                        <span className="ml-2 font-mono text-white">
                          {(() => {
                            // Check local updates first, then database
                            const localUpdate = localRightsUpdates[release.id];
                            if (localUpdate?.isrc !== undefined) {
                              return localUpdate.isrc || 'Henüz girilmedi';
                            }
                            const rights = Array.isArray(release.rights) ? release.rights[0] : release.rights;
                            return rights?.isrc || 'Henüz girilmedi';
                          })()}
                        </span>
                      </div>
                      <div className="p-3 bg-white/5 rounded-lg">
                        <span className="text-white/70">UPC:</span>
                        <span className="ml-2 font-mono text-white">
                          {(() => {
                            // Check local updates first, then database
                            const localUpdate = localRightsUpdates[release.id];
                            if (localUpdate?.upc !== undefined) {
                              return localUpdate.upc || 'Henüz girilmedi';
                            }
                            const rights = Array.isArray(release.rights) ? release.rights[0] : release.rights;
                            return rights?.upc || 'Henüz girilmedi';
                          })()}
                        </span>
                      </div>
                      <div className="p-3 bg-white/5 rounded-lg">
                        <span className="text-white/70">Phonogram Owner:</span>
                        <span className="ml-2 text-white">Distnary</span>
                      </div>
                      <div className="p-3 bg-white/5 rounded-lg">
                        <span className="text-white/70">Composition Owner:</span>
                        <span className="ml-2 text-white">Distnary</span>
                      </div>
                    </div>
                    
                    {/* ISRC/UPC Edit Button */}
                    <div className="mt-4">
                      <button
                        onClick={() => handleAddIsrcUpc(release.id)}
                        className="px-4 py-2 bg-orange-500/20 border border-orange-500/30 rounded-lg text-sm font-medium text-orange-300 hover:bg-orange-500/30 focus:outline-none focus:ring-2 focus:ring-orange-500 transition-all duration-200 flex items-center"
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        ISRC/UPC Düzenle
                      </button>
                    </div>
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex justify-between items-center">
                    {/* Detail Button - Always visible */}
                    <Link
                      to={`/dashboard/admin/tracks/view/${release.id}`}
                      className="px-6 py-3 bg-white/10 border border-white/20 rounded-xl text-sm font-medium text-white hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/30 transition-all duration-200 flex items-center"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      {t('common.viewDetails')}
                    </Link>

                    {/* Action buttons for each tab */}
                    <div className="flex space-x-3">
                      {activeTab === 'pending' && (
                        <>
                          <button
                            onClick={() => handleReject(release.id)}
                            className="px-6 py-3 bg-red-500/20 border border-red-500/30 rounded-xl text-sm font-medium text-red-300 hover:bg-red-500/30 focus:outline-none focus:ring-2 focus:ring-red-500 transition-all duration-200"
                          >
                            {t('common.reject')}
                          </button>
                          <button
                            onClick={() => handleApprove(release.id)}
                            className="px-6 py-3 bg-gradient-to-r from-green-500 to-green-600 rounded-xl text-sm font-medium text-white hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-green-500 transition-all duration-200 transform hover:scale-105"
                          >
                            {t('admin.approve')}
                          </button>
                        </>
                      )}
                      
                      {activeTab === 'approved' && (
                        <button
                          onClick={() => handleSubmitToPlatform(release.id)}
                          className="px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl text-sm font-medium text-white hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200 transform hover:scale-105"
                        >
                          {t('admin.submitToPlatform')}
                        </button>
                      )}
                      
                      {activeTab === 'submitted' && (
                        <div className="flex items-center space-x-3">
                          <button
                            onClick={() => handleReject(release.id)}
                            className="px-6 py-3 bg-red-500/20 border border-red-500/30 rounded-xl text-sm font-medium text-red-300 hover:bg-red-500/30 focus:outline-none focus:ring-2 focus:ring-red-500 transition-all duration-200"
                          >
                            {t('common.reject')}
                          </button>
                          <button
                            onClick={() => handlePublish(release.id)}
                            className="px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl text-sm font-medium text-white hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-green-500 transition-all duration-200 transform hover:scale-105"
                          >
                            {t('admin.publish')}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="glass-card mt-6">
                <div className="px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className="text-sm text-white/70">
                    Gösterilen: {startIndex + 1}-{Math.min(endIndex, allCurrentReleases.length)} / {allCurrentReleases.length}
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                        currentPage === 1
                          ? 'bg-white/5 text-white/30 cursor-not-allowed'
                          : 'bg-white/10 text-white hover:bg-white/20'
                      }`}
                    >
                      Önceki
                    </button>

                    <div className="flex items-center space-x-1">
                      {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                        let pageNum: number;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }

                        return (
                          <button
                            key={pageNum}
                            onClick={() => setCurrentPage(pageNum)}
                            className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${
                              currentPage === pageNum
                                ? 'bg-orange-500 text-white'
                                : 'bg-white/10 text-white hover:bg-white/20'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                    </div>

                    <button
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                      className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                        currentPage === totalPages
                          ? 'bg-white/5 text-white/30 cursor-not-allowed'
                          : 'bg-white/10 text-white hover:bg-white/20'
                      }`}
                    >
                      Sonraki
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Reject Modal */}
      <RejectModal
        isOpen={rejectModalOpen}
        releaseTitle={selectedReleaseForReject ? releases.find(r => r.id === selectedReleaseForReject)?.title || '' : ''}
        onConfirm={handleRejectConfirm}
        onCancel={handleRejectCancel}
      />

      {/* ISRC/UPC Modal */}
      {isrcModalOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-gray-900/95 backdrop-blur-lg border border-white/20 rounded-2xl p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-white">ISRC/UPC Düzenle</h3>
              <button
                onClick={handleIsrcUpcCancel}
                className="text-white/60 hover:text-white transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">
                  ISRC Kodu
                </label>
                <input
                  type="text"
                  value={isrcInput}
                  onChange={(e) => setIsrcInput(e.target.value)}
                  placeholder="Örn: TRXXX2501234"
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
                <p className="text-xs text-white/60 mt-1">
                  Uluslararası Standart Recording Code
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">
                  UPC Kodu
                </label>
                <input
                  type="text"
                  value={upcInput}
                  onChange={(e) => setUpcInput(e.target.value)}
                  placeholder="Örn: 123456789012"
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
                <p className="text-xs text-white/60 mt-1">
                  Universal Product Code (12 haneli)
                </p>
              </div>
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={handleIsrcUpcCancel}
                className="flex-1 px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white font-medium hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/30 transition-all duration-200"
              >
                İptal
              </button>
              <button
                onClick={handleIsrcUpcSave}
                disabled={isUpdating}
                className={`flex-1 px-4 py-3 rounded-xl text-white font-medium focus:outline-none focus:ring-2 focus:ring-orange-500 transition-all duration-200 ${
                  isUpdating 
                    ? 'bg-gray-500 cursor-not-allowed' 
                    : 'bg-gradient-to-r from-orange-500 to-orange-600 hover:shadow-lg transform hover:scale-105'
                }`}
              >
                {isUpdating ? 'Kaydediliyor...' : 'Kaydet'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PendingReleases; 